import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { resetStoreSpecificState } from "@/store/actions/storeActions";
import { useAuth } from "@/hooks/useAuth";
import type { AppDispatch, RootState } from "@/store/store";
import type { AccessRole } from "@/types/auth";

export default function StoreSelectorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [attemptingStoreId, setAttemptingStoreId] = useState<string | null>(
    null,
  );

  const {
    authPhase,
    statusLoading,
    isAuthenticated,
    stores,
    adminAuth,
    error,
    currentPhoneNumber,
    switchStore,
    logout,
  } = useAuth("store-selector");
  const currentStoreId = useSelector(
    (state: RootState) => state.auth.currentStoreId,
  );
  const isAuthorizing = useSelector((state: RootState) => state.auth.isLoading);

  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (!errorParam) {
      return;
    }

    const errorMessages: Record<string, string> = {
      unauthorized: "You don't have access to that store.",
      "store-mismatch":
        "The requested store doesn't match your current access.",
      "invalid-store-type": "Invalid store type in the URL.",
      "no-store-selected": "Please select a store to continue.",
    };
    setErrorMessage(errorMessages[errorParam] || "Access denied.");
  }, [searchParams]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error]);

  useEffect(() => {
    if (!attemptingStoreId) {
      return;
    }

    if (statusLoading) {
      return;
    }

    if (error) {
      setAttemptingStoreId(null);
      return;
    }

    if (currentStoreId === attemptingStoreId) {
      setAttemptingStoreId(null);
    }
  }, [attemptingStoreId, currentStoreId, error, statusLoading]);

  const handleStoreSelect = (store: AccessRole) => {
    setAttemptingStoreId(store.storeId);
    dispatch(resetStoreSpecificState());
    setErrorMessage(null);
    switchStore(store);
  };

  if (statusLoading || authPhase === "checkingStatus") {
    return (
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-auth-text-muted">Checking authentication...</p>
      </div>
    );
  }

  if (!isAuthenticated || stores.length === 0) {
    return null;
  }

  return (
    <>
      <div className="text-center mb-8">
        <img
          src="/assets/auth/logo.png"
          alt="Shiphouse Logo"
          width={90}
          height={90}
          className="mx-auto mb-4"
        />
        <h1 className="text-3xl font-semibold text-auth-text mb-2">
          Select Your Store
        </h1>
        {adminAuth && (
          <p className="text-auth-text-muted">
            Welcome back, {adminAuth.phone}
          </p>
        )}
        <div className="mt-6 flex items-center justify-between gap-4 rounded-2xl border border-border bg-card px-4 py-3 text-left shadow-theme-sm">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Logged in as
            </p>
            <p className="truncate text-sm font-medium text-foreground">
              {currentPhoneNumber || "Phone number unavailable"}
            </p>
          </div>
          <button
            type="button"
            onClick={logout}
            className="shrink-0 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            Log out
          </button>
        </div>
        {errorMessage && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">{errorMessage}</p>
          </div>
        )}
      </div>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
        <button
          onClick={() => navigate("/auth/new-store")}
          className="w-full p-4 bg-primary/10 border-2 border-primary/30 rounded-xl text-left hover:bg-primary/20 hover:border-primary/50 transition-all duration-300 group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-auth-text font-medium">Create New Store</h3>
                <p className="text-auth-text-muted text-sm">
                  Set up a new store for your business
                </p>
              </div>
            </div>
            <div className="text-primary group-hover:translate-x-1 transition-transform duration-300">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </button>

        {stores.map((store) => {
          const isCurrentlyLoading =
            isAuthorizing && attemptingStoreId === store.storeId;

          return (
            <button
              key={store.storeId}
              onClick={() => handleStoreSelect(store)}
              disabled={isAuthorizing}
              className={`w-full p-4 border border-auth-border rounded-xl text-left transition-all duration-300 group ${
                isAuthorizing
                  ? "bg-auth-surface/50 cursor-not-allowed opacity-60"
                  : "bg-auth-surface hover:bg-auth-surface-dark hover:border-primary/30"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <svg
                      className="h-5 w-5 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 text-left">
                    <h3 className="truncate text-auth-text font-medium">
                      {store.storeName || store.storeId}
                    </h3>
                    <p className="truncate text-auth-text-muted text-sm">
                      <span className="capitalize">{store.role}</span>
                      {store.domain && (
                        <>
                          {" • "}
                          {store.domain.length > 30
                            ? `${store.domain.substring(0, 30)}...`
                            : store.domain}
                        </>
                      )}
                    </p>
                  </div>
                </div>
                {isCurrentlyLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                ) : (
                  <div className="text-primary transition-transform duration-300 group-hover:translate-x-1">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}
